/**
 * @fileoverview The student's Home ("Studio Quiet"; C1, decided Nov 1, 2026):
 * what to practice this week first, each item with its own Log, the streak in
 * a line, then the next lesson and the latest one. The lesson feed lives on
 * Lessons; the streak card, the totals and the log on Progress. The web twin
 * of mobile/src/screens/StudentDashboardScreen.js.
 */

import { useCallback, useState, useEffect, useContext, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { StudentAuthContext } from "@/App";
import studentApi, { getPracticeStats, getStudentErrorMessage, listPracticeLogs } from "@/lib/studentApi";
import { firstName } from "@/lib/userName";
import { ASSIGNMENTS_ENABLED, BILLING_ENABLED, SCHEDULING_ENABLED, practiceLogsEnabled } from "@/lib/featureFlags";
import { formatTime12h, relativeDayLabel, shortDateLabel } from "@/lib/schedule";
import { lessonDay, latestPracticeLesson, minutesFor, practiceItems, streakLine } from "@/lib/studentPractice";
import { Card, Icon, Button } from "@/components/studio/primitives";
import { SHeader } from "@/components/studio/studentUi";
import { StudentAssignmentsChecklist } from "@/components/StudentAssignmentsChecklist";
import { useStudentPractice } from "@/components/StudentPracticeContext";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** "2026-10-28" → "Oct 28", read from its own parts so no time zone moves it. */
const shortDay = (day) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day || "");
  return m ? `${MONTHS[Number(m[2]) - 1]} ${Number(m[3])}` : "";
};

/** The first word of a name, for "from Oct 28 · Robin". */
const firstWord = (name) => String(name || "").trim().split(/\s+/)[0] || "";

const StudentDashboard = () => {
  const navigate = useNavigate();
  const { student } = useContext(StudentAuthContext) || {};
  const { openLog, version } = useStudentPractice();
  const canLog = practiceLogsEnabled(student);
  const [lessons, setLessons] = useState([]);
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [portfolioReady, setPortfolioReady] = useState(false);
  const [upcoming, setUpcoming] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  // Phase 2 self-service: occurrence being rescheduled + its proposal form
  const [rescheduling, setRescheduling] = useState(null); // {slotId, date}
  const [proposal, setProposal] = useState({ date: "", time: "", note: "" });
  const [acting, setActing] = useState(false);

  const refreshAssignments = useCallback((signal) => {
    if (!ASSIGNMENTS_ENABLED) return;
    studentApi
      .get("/student/assignments?status=open", signal ? { signal } : {})
      .then((res) => setAssignments(res.data || []))
      .catch(() => {});
  }, []);

  const refreshUpcoming = useCallback(() => {
    if (!SCHEDULING_ENABLED) return;
    studentApi
      .get("/student/schedule")
      .then((res) => setUpcoming(res.data?.occurrences || []))
      .catch(() => {});
  }, []);

  const cancelOccurrence = async (occ) => {
    if (!window.confirm(`Cancel your ${relativeDayLabel(occ.effective_date)} ${formatTime12h(occ.time_local)} lesson?`)) {
      return;
    }
    setActing(true);
    try {
      await studentApi.post(`/student/schedule/occurrences/${occ.slot_id}/${occ.date}/cancel`, {});
      toast.success("Lesson cancelled — your teacher has been notified");
      refreshUpcoming();
    } catch (error) {
      toast.error(getStudentErrorMessage(error, "Could not cancel this lesson"));
    } finally {
      setActing(false);
    }
  };

  const submitReschedule = async (occ) => {
    if (!proposal.date || !proposal.time) {
      toast.error("Pick the date and time you'd like instead");
      return;
    }
    setActing(true);
    try {
      await studentApi.post(`/student/schedule/occurrences/${occ.slot_id}/${occ.date}/reschedule-request`, {
        proposed_date: proposal.date,
        proposed_time_local: proposal.time,
        note: proposal.note || undefined,
      });
      toast.success("Request sent — your teacher will confirm the new time");
      setRescheduling(null);
      setProposal({ date: "", time: "", note: "" });
      refreshUpcoming();
    } catch (error) {
      toast.error(getStudentErrorMessage(error, "Could not send the request"));
    } finally {
      setActing(false);
    }
  };

  useEffect(() => {
    const controller = new AbortController();
    const fetchData = async () => {
      try {
        const [lessonsRes, portfolioRes] = await Promise.all([
          studentApi.get("/student/lessons?limit=20", { signal: controller.signal }),
          studentApi.get("/student/portfolio-status", { signal: controller.signal }).catch(() => null),
        ]);
        setLessons(lessonsRes.data || []);
        const ps = portfolioRes?.data;
        setPortfolioReady(ps?.can_update === true && ps?.generation_count === 0);
      } catch (err) {
        if (controller.signal.aborted) return;
        console.error("Failed to load dashboard:", err);
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };
    fetchData();
    // Upcoming lessons are best-effort — never block the dashboard.
    if (SCHEDULING_ENABLED) {
      studentApi
        .get("/student/schedule", { signal: controller.signal })
        .then((res) => setUpcoming(res.data?.occurrences || []))
        .catch(() => {});
    }
    if (BILLING_ENABLED) {
      studentApi
        .get("/student/invoices", { signal: controller.signal })
        .then((res) => setInvoices(res.data?.invoices || []))
        .catch(() => {});
    }
    refreshAssignments(controller.signal);
    return () => controller.abort();
  }, [refreshAssignments]);

  // The logs and the streak: read on show and again after every log made
  // anywhere in the portal (the Log practice dialog moves `version`).
  useEffect(() => {
    if (!canLog) return undefined;
    const controller = new AbortController();
    const { signal } = controller;
    listPracticeLogs({ signal }).then(setLogs).catch(() => {});
    getPracticeStats({ signal })
      .then((data) => {
        setStats(data);
        // A milestone is celebrated once, by whichever read first notices it.
        (data?.newly_awarded || []).forEach((m) => toast.success(m.title));
      })
      .catch(() => {});
    return () => controller.abort();
  }, [canLog, version]);

  const greeting = firstName(student);
  const practiceLesson = useMemo(() => latestPracticeLesson(lessons), [lessons]);
  const items = useMemo(() => practiceItems(practiceLesson), [practiceLesson]);
  const since = lessonDay(practiceLesson);
  const latest = useMemo(
    () => lessons.reduce((best, l) => (!best || lessonDay(l) > lessonDay(best) ? l : best), null),
    [lessons],
  );
  const line = canLog ? streakLine(stats) : null;
  const practiceMeta = practiceLesson
    ? [`from ${shortDay(since)}`, firstWord(practiceLesson.teacher_name)].filter(Boolean).join(" · ")
    : "";

  return (
    <div className="mx-auto max-w-[1120px] px-6 pb-32 pt-11 md:px-12" data-testid="student-dashboard">
      <SHeader eyebrow="Student portal" title={greeting ? `Hi, ${greeting}` : "Hi"} className="mb-3" />
      {line ? (
        <p className="mb-8 flex items-center gap-2 font-sans text-[15px] text-[var(--text-tertiary)]" data-testid="home-streak">
          <Icon name="flame" size={16} className="text-[var(--accent-yellow-text)]" />
          {line}
        </p>
      ) : (
        <div className="mb-8" />
      )}

      {/* Practice this week (C1): the latest lesson's practice items, each with Log. */}
      <Card padding={0} className="mb-6 overflow-hidden" data-testid="practice-this-week">
        <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-0.5 px-6 pb-2 pt-5">
          <h2 className="whitespace-nowrap font-sans text-lg font-semibold tracking-[-0.01em] text-[var(--text-primary)]">Practice this week</h2>
          {practiceMeta && <span className="font-sans text-[13px] text-[var(--text-tertiary)]">{practiceMeta}</span>}
        </div>
        {loading ? (
          <div className="space-y-2 px-6 pb-5">
            {[1, 2].map((i) => <div key={i} className="skeleton h-12 rounded-xl" />)}
          </div>
        ) : items.length ? (
          items.map((item, i) => {
            const minutes = minutesFor(logs, item, since);
            return (
              <div
                key={item}
                className={[
                  "flex items-center gap-4 px-6 py-3.5",
                  i < items.length - 1 ? "border-b border-[var(--studio-divider)]" : "",
                ].join(" ")}
                data-testid={`practice-item-${i}`}
              >
                <span className="min-w-0 flex-1 font-sans text-[15px] leading-snug text-[var(--text-primary)]">{item}</span>
                {canLog && minutes > 0 ? (
                  <button
                    type="button"
                    onClick={() => openLog({ item })}
                    aria-label={`${minutes} minutes logged. Log more for ${item}`}
                    className="flex shrink-0 items-center gap-1.5 rounded-full px-2 py-1.5 font-sans text-[13px] font-semibold text-[var(--state-success)] hover:bg-[var(--studio-raised)]"
                    data-testid={`practice-item-logged-${i}`}
                  >
                    <Icon name="check" size={15} strokeWidth={2.4} />
                    {minutes} min
                  </button>
                ) : canLog ? (
                  <Button
                    variant="ghost"
                    icon="plus"
                    onPress={() => openLog({ item })}
                    aria-label={`Log practice for ${item}`}
                    className="shrink-0 px-4 py-2 text-[14px]"
                    data-testid={`practice-item-log-${i}`}
                  >
                    Log
                  </Button>
                ) : null}
              </div>
            );
          })
        ) : (
          <p className="px-6 pb-5 pt-1 font-sans text-[14px] text-[var(--text-tertiary)]" data-testid="practice-empty">
            {lessons.length
              ? "What your teacher asks you to practice shows here after a lesson."
              : "After your first lesson, what to practice shows here."}
          </p>
        )}
      </Card>

      {/* Portfolio-ready banner: written under Progress, then shared from there. */}
      {portfolioReady && (
        <Card
          hover
          onPress={() => navigate("/student/progress/share")}
          padding={16}
          className="mb-6 flex items-center gap-4 border-[var(--accent-yellow-border)] bg-[var(--accent-yellow-dim)]"
        >
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[var(--accent-yellow-dim)]">
            <Icon name="trophy" size={20} className="text-[var(--accent-yellow-text)]" />
          </div>
          <div className="flex-1">
            <p className="font-sans text-sm font-semibold text-[var(--accent-yellow-text)]">Your portfolio is ready</p>
            <p className="mt-0.5 font-sans text-[13px] text-[var(--text-tertiary)]">
              Write it, then choose who sees it.
            </p>
          </div>
          <Icon name="sparkle" size={16} className="shrink-0 text-[var(--accent-yellow-text)]" />
        </Card>
      )}

      {/* Practice assignments checklist (Spec 01) */}
      {ASSIGNMENTS_ENABLED && assignments.length > 0 && (
        <div className="mb-6">
          <StudentAssignmentsChecklist assignments={assignments} onChanged={refreshAssignments} />
        </div>
      )}

      {/* Next lesson (Spec 04 — read-only view across linked teachers) */}
      {SCHEDULING_ENABLED && upcoming.length > 0 && (
        <Card padding={18} className="mb-6" data-testid="next-lesson-card">
          <div className="flex items-center gap-3.5">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-[12px] bg-[var(--accent-yellow-dim)]">
              <Icon name="calendar" size={20} className="text-[var(--accent-yellow-text)]" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="font-sans text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-tertiary)]">Next lesson</div>
              <p className="mt-0.5 truncate font-sans text-base font-medium text-[var(--text-primary)]">
                {relativeDayLabel(upcoming[0].effective_date)} {formatTime12h(upcoming[0].time_local)}
                {upcoming[0].teacher_name ? ` with ${upcoming[0].teacher_name}` : ""}
                {upcoming[0].class_name ? ` · ${upcoming[0].class_name}` : ""}
              </p>
            </div>
          </div>
          {/* Phase 2 self-service actions for the next occurrence */}
          {(() => {
            const occ = upcoming[0];
            const isRescheduling =
              rescheduling && rescheduling.slotId === occ.slot_id && rescheduling.date === occ.date;
            return (
              <div className="mt-3.5 border-t border-[var(--studio-divider)] pt-3">
                {occ.externally_managed ? (
                  <span
                    className="font-sans text-[12px] text-[var(--text-quaternary)]"
                    data-testid="externally-managed-note"
                  >
                    Scheduled through your teacher’s booking system — contact them to change it
                  </span>
                ) : occ.pending_reschedule ? (
                  <span
                    className="inline-flex items-center gap-1.5 rounded-full border border-[var(--accent-yellow-border)] bg-[var(--accent-yellow-dim)] px-3 py-1 font-sans text-[12px] font-medium text-[var(--accent-yellow-text)]"
                    data-testid="pending-reschedule-badge"
                  >
                    <Icon name="clock" size={12} /> New time requested — waiting for your teacher
                  </span>
                ) : isRescheduling ? (
                  <div className="space-y-2" data-testid="reschedule-form">
                    <div className="grid grid-cols-2 gap-2">
                      <input
                        type="date"
                        value={proposal.date}
                        onChange={(e) => setProposal((p) => ({ ...p, date: e.target.value }))}
                        className="rounded-lg border border-[var(--studio-divider)] bg-[var(--studio-base)] px-3 py-2 font-sans text-[13px] text-[var(--text-primary)]"
                        data-testid="reschedule-date"
                      />
                      <input
                        type="time"
                        value={proposal.time}
                        onChange={(e) => setProposal((p) => ({ ...p, time: e.target.value }))}
                        className="rounded-lg border border-[var(--studio-divider)] bg-[var(--studio-base)] px-3 py-2 font-sans text-[13px] text-[var(--text-primary)]"
                        data-testid="reschedule-time"
                      />
                    </div>
                    <input
                      value={proposal.note}
                      onChange={(e) => setProposal((p) => ({ ...p, note: e.target.value }))}
                      placeholder="Add a note (optional)"
                      className="w-full rounded-lg border border-[var(--studio-divider)] bg-[var(--studio-base)] px-3 py-2 font-sans text-[13px] text-[var(--text-primary)] placeholder:text-[var(--text-quaternary)]"
                    />
                    <div className="flex gap-2">
                      <button
                        type="button"
                        disabled={acting}
                        onClick={() => submitReschedule(occ)}
                        className="rounded-full bg-[var(--accent-yellow)] px-4 py-1.5 font-sans text-[13px] font-semibold text-[var(--text-inverse)] disabled:opacity-50"
                        data-testid="send-reschedule-request"
                      >
                        Send request
                      </button>
                      <button
                        type="button"
                        onClick={() => setRescheduling(null)}
                        className="rounded-full border border-[var(--studio-divider)] px-4 py-1.5 font-sans text-[13px] text-[var(--text-secondary)]"
                      >
                        Never mind
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        setRescheduling({ slotId: occ.slot_id, date: occ.date });
                        setProposal({ date: occ.effective_date, time: occ.time_local, note: "" });
                      }}
                      className="rounded-full border border-[var(--studio-divider)] bg-[var(--studio-raised)] px-3.5 py-1.5 font-sans text-[13px] text-[var(--text-secondary)] hover:bg-[var(--studio-interactive)]"
                      data-testid="request-reschedule-button"
                    >
                      Request new time
                    </button>
                    {occ.can_cancel ? (
                      <button
                        type="button"
                        disabled={acting}
                        onClick={() => cancelOccurrence(occ)}
                        className="rounded-full border border-[var(--studio-divider)] px-3.5 py-1.5 font-sans text-[13px] text-[var(--text-tertiary)] hover:text-[var(--state-danger)] disabled:opacity-50"
                        data-testid="cancel-occurrence-button"
                      >
                        Cancel lesson
                      </button>
                    ) : (
                      <span className="font-sans text-[12px] text-[var(--text-quaternary)]">
                        {occ.class_id
                          ? "Group lessons are managed by your teacher"
                          : `Cancellations close ${occ.cutoff_hours ?? 24}h before — contact your teacher`}
                      </span>
                    )}
                  </div>
                )}
              </div>
            );
          })()}
          {upcoming.length > 1 && (
            <ul className="mt-3.5 space-y-2 border-t border-[var(--studio-divider)] pt-3.5">
              {upcoming.slice(1, 4).map((occ) => (
                <li key={`${occ.slot_id}-${occ.date}`} className="flex items-center justify-between gap-3 font-sans text-[13px]">
                  <span className="truncate text-[var(--text-secondary)]">
                    {shortDateLabel(occ.effective_date)} · {occ.teacher_name || occ.class_name || "Lesson"}
                  </span>
                  <span className="shrink-0 font-mono text-[var(--text-tertiary)]">{formatTime12h(occ.time_local)}</span>
                </li>
              ))}
            </ul>
          )}
        </Card>
      )}

      {/* Payments (Spec 05) — invoices fan in across linked teachers */}
      {BILLING_ENABLED && invoices.length > 0 && (
        <Card padding={18} className="mb-6" data-testid="payments-card">
          <div className="mb-3 flex items-center gap-2">
            <Icon name="receipt" size={16} className="text-[var(--accent-yellow-text)]" />
            <span className="font-sans text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-tertiary)]">Payments</span>
          </div>
          <ul className="space-y-3">
            {invoices.slice(0, 4).map((invoice) => (
              <li key={invoice.invoice_id} className="flex flex-wrap items-center gap-3" data-testid={`portal-invoice-${invoice.invoice_id}`}>
                <span className="font-mono text-sm text-[var(--text-primary)]">
                  {(invoice.amount_cents / 100).toLocaleString("en-US", { style: "currency", currency: "USD" })}
                </span>
                <span className="min-w-0 flex-1 truncate font-sans text-[13px] text-[var(--text-tertiary)]">
                  {/* Name the ISSUER (the org for org-billed programs, else the
                      teacher) + the program so the payer sees who's billing them
                      (Spec 12 WP4). */}
                  {invoice.issuer_name || invoice.teacher_name}
                  {invoice.program_title ? ` · ${invoice.program_title}` : ""} · due {invoice.due_date}
                </span>
                {invoice.status === "paid" ? (
                  <span className="font-sans text-[12px] font-semibold text-[var(--state-success)]">Paid</span>
                ) : invoice.sent_to_parent ? (
                  <span className="font-sans text-[12px] text-[var(--text-quaternary)]" data-testid="sent-to-parent">
                    An invoice was sent to your parent
                  </span>
                ) : invoice.hosted_invoice_url ? (
                  <a
                    href={invoice.hosted_invoice_url}
                    target="_blank"
                    rel="noreferrer"
                    className="rounded-full bg-[var(--accent-yellow)] px-4 py-1.5 font-sans text-[13px] font-semibold text-[var(--text-inverse)]"
                    data-testid={`pay-${invoice.invoice_id}`}
                  >
                    Pay
                  </a>
                ) : (
                  <span className="font-sans text-[12px] text-[var(--text-quaternary)]">{invoice.status}</span>
                )}
              </li>
            ))}
          </ul>
        </Card>
      )}

      {/* The latest lesson; every lesson is on Lessons. */}
      {latest && (
        <>
          <Card hover onPress={() => navigate(`/student/lessons/${latest.lesson_id}`)} padding={18} data-testid="latest-lesson">
            <div className="flex items-center gap-4">
              <div className="min-w-0 flex-1">
                <div className="font-sans text-[13px] text-[var(--text-tertiary)]">Latest lesson · {shortDay(lessonDay(latest))}</div>
                <div className="mt-0.5 truncate font-sans text-base font-semibold text-[var(--text-primary)]">{latest.title || "Lesson"}</div>
              </div>
              <Icon name="chevronRight" size={18} className="text-[var(--text-tertiary)]" />
            </div>
          </Card>
          <div className="mt-4 text-center">
            <button
              type="button"
              onClick={() => navigate("/student/lessons")}
              className="font-sans text-[15px] font-semibold text-[var(--accent-yellow-text)] hover:underline"
              data-testid="all-lessons"
            >
              All lessons
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default StudentDashboard;
